import React from 'react';
import { Alert, Divider, Flex, Modal, Radio, Typography, theme } from 'antd';

import { presentAsDownload } from '../../../../assets/lib';
import {
  useExportDiscoverabilityPagesMutation,
  type PagesToReviewSortColumn,
  type PagesToReviewSortDirection,
  type PagesToReviewTab,
  type PagesToReviewUrlSearchCondition,
} from '../api/discoverabilityPagesApi';
import type { DiscoverabilityPagesScope } from '../api/discoverabilityPagesScope';
import {
  capExportWindowToThirtyDays,
  EXPORT_WINDOW_CAP_DAYS,
} from '../exportWindowCap';
import {
  exportedDataRowCount,
  pagesToReviewExportFilename,
} from '../pagesToReviewCsvExport';
import { exportScopeSummary } from '../exportScopeSummary';
import { useAiCrawlerLineup } from '../../shared';
import useTrackDiscoverabilityEvent from '../hooks/useTrackDiscoverabilityEvent';

const SCOPE_OPTIONS: {
  tab: PagesToReviewTab;
  label: string;
  description: string;
}[] = [
  {
    tab: 'access_blocked',
    label: 'Access blocked pages',
    description:
      'Pages where crawlers were blocked or the site failed to respond.',
  },
  {
    tab: 'access_limited',
    label: 'Access limited pages',
    description:
      'Pages crawlers reached, but that answered with a redirect or an error.',
  },
  {
    tab: 'all_pages',
    label: 'All crawled pages',
    description:
      'Every page AI crawlers requested in the period, including healthy ones.',
  },
];

export type PagesToReviewExportFilters = {
  sortBy: PagesToReviewSortColumn;
  sortDirection: PagesToReviewSortDirection;
  urlSearch: string | undefined;
  urlSearchCondition: PagesToReviewUrlSearchCondition | undefined;
  statusEquals: number | undefined;
  statusFrom: number | undefined;
  statusTo: number | undefined;
};

export type PagesToReviewExportModalProps = {
  open: boolean;
  /** Preselects the scope, but the user's choice is what gets exported. */
  activeTab: PagesToReviewTab;
  scope: DiscoverabilityPagesScope;
  filters: PagesToReviewExportFilters;
  /** The applied single-domain filter, carried into the filename. */
  domain: string | undefined;
  onClose: () => void;
};

// An export that comes back with only its header row matched nothing. Reported
// rather than saved — an empty file with no explanation is worse than a message.
type ExportOutcome = 'empty' | 'failed' | undefined;

function PagesToReviewExportModal({
  open,
  activeTab,
  scope,
  filters,
  domain,
  onClose,
}: PagesToReviewExportModalProps) {
  const { token } = theme.useToken();
  const trackEvent = useTrackDiscoverabilityEvent();
  const lineup = useAiCrawlerLineup();
  const [exportPages, { isLoading }] = useExportDiscoverabilityPagesMutation();
  const [selectedTab, setSelectedTab] = React.useState<PagesToReviewTab>(activeTab);
  const [outcome, setOutcome] = React.useState<ExportOutcome>(undefined);

  // Not named `window`: that would shadow the global inside this component.
  const effectiveWindow = capExportWindowToThirtyDays(scope);
  // Summarised from the CAPPED window, so the Dates row states what will
  // actually be read rather than contradicting the narrowing notice below it.
  const scopeRows = exportScopeSummary(lineup, { ...scope, ...effectiveWindow });

  const handleDownload = async () => {
    setOutcome(undefined);

    const csv = await exportPages({
      ...scope,
      ...effectiveWindow,
      tab: selectedTab,
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
      urlSearch: filters.urlSearch,
      urlSearchCondition: filters.urlSearchCondition,
      statusEquals: filters.statusEquals,
      statusFrom: filters.statusFrom,
      statusTo: filters.statusTo,
    })
      .unwrap()
      .catch(() => undefined);

    if (csv === undefined) {
      setOutcome('failed');
      return;
    }

    const rowCount = exportedDataRowCount(csv);
    if (rowCount === 0) {
      setOutcome('empty');
      return;
    }

    presentAsDownload(
      csv,
      pagesToReviewExportFilename({
        tab: selectedTab,
        domain,
        dateTimeFrom: effectiveWindow.dateTimeFrom,
        dateTimeTo: effectiveWindow.dateTimeTo,
      })
    );
    trackEvent('Discoverability: Pages To Review CSV Exported', {
      tab: selectedTab,
      row_count: rowCount,
      url_search_condition: filters.urlSearchCondition,
      has_url_search: filters.urlSearch !== undefined,
      has_status_filter:
        filters.statusEquals !== undefined ||
        filters.statusFrom !== undefined ||
        filters.statusTo !== undefined,
      sort_by: filters.sortBy,
      sort_direction: filters.sortDirection,
      window_narrowed: effectiveWindow.narrowed,
    });
    onClose();
  };

  return (
    <Modal
      title="Export Pages to Review"
      open={open}
      onCancel={onClose}
      okText="Download CSV"
      onOk={handleDownload}
      okButtonProps={{ loading: isLoading, disabled: isLoading }}
      cancelButtonProps={{ disabled: isLoading }}
    >
      <Flex vertical gap={token.marginSM}>
        <Typography.Text>Choose which pages to export.</Typography.Text>

        <Radio.Group
          value={selectedTab}
          onChange={(event) => setSelectedTab(event.target.value)}
        >
          <Flex vertical gap={token.marginXS}>
            {SCOPE_OPTIONS.map((option) => (
              <Radio key={option.tab} value={option.tab}>
                {option.label}
                <Typography.Text
                  type="secondary"
                  style={{ display: 'block', fontSize: token.fontSizeSM }}
                >
                  {option.description}
                </Typography.Text>
              </Radio>
            ))}
          </Flex>
        </Radio.Group>

        <Divider style={{ margin: 0 }} />

        <Flex vertical gap={token.marginXXS}>
          <Typography.Text>This export covers:</Typography.Text>
          {scopeRows.map((row) => (
            <Flex key={row.label} gap={token.marginSM}>
              <Typography.Text
                type="secondary"
                style={{ width: 90, flexShrink: 0 }}
              >
                {row.label}
              </Typography.Text>
              <Typography.Text>{row.value}</Typography.Text>
            </Flex>
          ))}
        </Flex>

        {/* The block above lists only the filter bar's three. Saying the table's
        own controls apply too keeps a partial list from reading as a complete
        one — a user with a URL search active would otherwise see no mention of
        it and reasonably conclude it was dropped. */}
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          The table&rsquo;s search, status filter and sort are applied too.
        </Typography.Text>

        {/* Only when trimmed, and without repeating the dates — the Dates row
        above already states the window, so this explains why it differs from
        the range the user picked rather than saying it twice. */}
        {effectiveWindow.narrowed && (
          <Typography.Text
            type="secondary"
            style={{ fontSize: token.fontSizeSM }}
          >
            {`You can export up to ${EXPORT_WINDOW_CAP_DAYS} days at once. Your filter spans ${effectiveWindow.requestedDays} days, so this file includes only the most recent ${EXPORT_WINDOW_CAP_DAYS} days.`}
          </Typography.Text>
        )}

        {outcome === 'empty' && (
          <Alert
            type="info"
            showIcon
            message="There are no rows to export for this selection. Pick another one, or widen the filters on the table."
          />
        )}
        {outcome === 'failed' && (
          <Alert
            type="error"
            showIcon
            message="The pages could not be exported. Please try again later."
          />
        )}
      </Flex>
    </Modal>
  );
}

export default PagesToReviewExportModal;
